import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '@frolight/components/Button';
import useAuthenticatedAPIServiceCall from '@frolight/hooks/useAuthenticatedAPIServiceCall';
import * as gatewayService from '@frolight/services/api/gatewayService';
import useLocalizedFormattedText from '@frolight/hooks/useLocalizedFormattedText';

const GatewayRegistrationConfirmation = ({ gateways, organisation, onRegistrationComplete }) => {
  const { lftext } = useLocalizedFormattedText();

  const [ gatewayRegistrationError, setGatewayRegistrationError ] = useState(null);
  const [ loading, setLoading ] = useState(false);
  const [ registrationComplete, setRegistrationComplete ] = useState(false);
  const [ confirmationRequested, setConfirmationRequested ] = useState(false);

  const authenticatedAPIServiceCall = useAuthenticatedAPIServiceCall();

  // Register gateways
  useEffect(() => {
    let mounted = true;
    const abortControllers = [];

    const registerGateways = async () => {
      setLoading(true);

      try {
        await Promise.all(gateways.map(async (gateway) => {
          const abortController = new AbortController();
          abortControllers.push(abortController);

          await authenticatedAPIServiceCall(
            gatewayService.registerGatewayToOrganisation,
            {
              abortController: abortController,
              opts: {
                gatewayId: gateway.id,
                organisationId: organisation.id
              }
            }
          );
        }));
        if (mounted) {
          setRegistrationComplete(true);
        }
      } catch (err) {
        setGatewayRegistrationError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (confirmationRequested && gateways.length) {
      registerGateways();
    }

    return () => {
      mounted = false;
      abortControllers.forEach(abortController => abortController.abort());
    }
  }, [confirmationRequested, gateways]);

  // Gateways registered
  useEffect(() => {
    if (registrationComplete && onRegistrationComplete) {
      onRegistrationComplete();
    }
  }, [registrationComplete, onRegistrationComplete]);

  const handleConfirm = (e) => {
    e.preventDefault();
    setConfirmationRequested(true);
  };

  const renderError = () => {
    if (gatewayRegistrationError) {
      return (
        <div className="feedback_message  feedback_message--error">
          {lftext('pages.gateway_registration.render_error')}
        </div>
      );
    }
    return null;
  };

  const renderContent = () => {
    if (gateways.length) {
      return (
        <>
          {lftext('pages.gateway_registration.add_devices', { organisation: organisation.name })}
          <ul>
            {
              gateways.map((gateway) => {
                return (
                  <li key={ `gateway-${gateway.id}` }>{ gateway.name }</li>
                );
              })
            }
          </ul>
          { renderError() }
          <p>
            <Button
              type="button"
              className="button"
              label={lftext('pages.gateway_registration.confirm_button')}
              priority="primary"
              onClick={ handleConfirm }
              disabled={ loading }
            />
          </p>
        </>
      );
    }
    return (
      <p>
        {lftext('pages.gateway_registration.no_devices')}
      </p>
    )
  }

  return (
    <div className="rich_text">
      <h1>
        {lftext('pages.gateway_registration.confirm_registration')}
      </h1>
      { renderContent() }
    </div>
  );

}

GatewayRegistrationConfirmation.propTypes = {
  gateways: PropTypes.arrayOf(PropTypes.object).isRequired,
  organisation: PropTypes.object.isRequired,
  onRegistrationComplete: PropTypes.func.isRequired,
  error: PropTypes.object
}

export default GatewayRegistrationConfirmation;
